import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BookingForm from '@/components/BookingForm';
import { ArrowLeft, CheckCircle, Phone, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceImages = {
  'junk-removal': 'https://images.unsplash.com/photo-1584786454449-ecc77738684c?w=1200&q=80',
  'deliveries': 'https://images.unsplash.com/photo-1616432043562-3671ea2e5242?w=1200&q=80',
  'moving': 'https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?w=1200&q=80'
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    fetchService();
    fetchAllServices();
  }, [slug]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      const found = response.data.find(s => s.id === slug);
      setService(found);
    } catch (error) {
      console.error('Error fetching service:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setAllServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleBookNow = () => {
    setShowBooking(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar onBookNow={() => {}} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#d4af37] hover:underline">
            ← Back to Services
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const otherServices = allServices.filter(s => s.id !== slug);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar onBookNow={handleBookNow} />
      
      {/* Hero Section with Image */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 z-0 h-96">
          <img 
            src={serviceImages[service.id]} 
            alt={service.name}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/services" 
            className="inline-flex items-center text-[#d4af37] hover:text-[#b8941f] mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Services
          </Link>

          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {service.name}
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Professional {service.name.toLowerCase()} services in Calgary and surrounding areas
          </p>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-16 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Description */}
          <div className="bg-[#1a1a1a] rounded-xl p-8 mb-12 border border-gray-800">
            <h2 className="text-3xl font-bold mb-6 text-[#d4af37]">About This Service</h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              {service.description}
            </p>
          </div>

          {/* Features */}
          <div className="bg-[#1a1a1a] rounded-xl p-8 mb-12 border border-gray-800">
            <h2 className="text-3xl font-bold mb-6 text-[#d4af37]">What We Offer</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {service.features.map((feature, idx) => (
                <div key={idx} className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-[#d4af37] mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#d4af37]/20 to-[#d4af37]/10 rounded-xl p-8 mb-12 border border-[#d4af37]/30 text-center">
            <h2 className="text-3xl font-bold mb-4 text-white">Ready to Book?</h2>
            <p className="text-gray-300 mb-6 text-lg">
              Get professional {service.name.toLowerCase()} services in Calgary today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleBookNow}
                className="bg-[#d4af37] text-black px-8 py-4 rounded-lg text-lg font-bold hover:bg-[#b8941f] transition"
                data-testid="service-book-button"
              >
                Book {service.name} Now
              </button>
              <a 
                href="tel:4038014638"
                className="inline-flex items-center justify-center bg-transparent border-2 border-[#d4af37] text-[#d4af37] px-8 py-4 rounded-lg text-lg font-bold hover:bg-[#d4af37] hover:text-black transition"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call (403) 801-4638
              </a>
            </div>
          </div>

          {/* Other Services */}
          {otherServices.length > 0 && (
            <div>
              <h2 className="text-3xl font-bold mb-8 text-center">
                Other <span className="gold-gradient">Services</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {otherServices.map((otherService) => (
                  <Link
                    key={otherService.id}
                    to={`/services/${otherService.id}`}
                    className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800 hover:border-[#d4af37] transition group"
                  >
                    <h3 className="text-xl font-bold mb-2 text-white group-hover:text-[#d4af37] transition">
                      {otherService.name}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {otherService.description.substring(0, 100)}...
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      
      {showBooking && (
        <BookingForm 
          isOpen={showBooking} 
          onClose={() => setShowBooking(false)}
          selectedService={service.name}
        />
      )}
    </div>
  );
};

export default ServiceDetail;
